import { HttpErrorResponse } from "@angular/common/http";
import { computed, inject, Injectable, signal, untracked } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TaskTrayService } from "app/modules/task-tray/task-tray.service";
import { TaskData } from "app/modules/task-tray/taskData";
import { catchError, EMPTY, Subject, switchMap, takeUntil, tap } from "rxjs";

@Injectable({ providedIn: 'root' })
export class TaskStateService {

    private _taskTrayService = inject(TaskTrayService);

    private _taskData = signal<TaskData | null>(null);
    private _loadingTaskId = signal<string | null>(null);

    readonly taskData = this._taskData.asReadonly();
    readonly isLoading = computed(() => this._loadingTaskId() !== null);

    /** Requests a task load. `switchMap` aborts the previous request. */
    private _requestTaskId$ = new Subject<string>();
    /** Aborts an in-flight request when the store is cleared. */
    private _cancel$ = new Subject<void>();

    constructor() {
        this._requestTaskId$.pipe(
            switchMap(taskId =>
                this._taskTrayService.getTaskData(taskId).pipe(
                    // takeUntil is on the INNER stream only, so a clear aborts the
                    // request without completing the outer subject.
                    takeUntil(this._cancel$),
                    tap(res => {
                        this._taskData.set(res);
                        this._loadingTaskId.set(null);
                    }),
                    catchError((err: HttpErrorResponse) => {
                        this._loadingTaskId.set(null);
                        // Task is definitively gone (completed / moved to the next
                        // stage), so drop it: permission-gated UI must fail closed
                        // rather than act on a task that no longer exists.
                        // Transient/network/5xx errors keep the last known task.
                        if (err?.status === 404 || err?.status === 410) {
                            this._taskData.set(null);
                        }
                        return EMPTY;
                    }),
                )
            ),
            takeUntilDestroyed(),
        ).subscribe();
    }

    setTaskData(data: TaskData) {
        this._taskData.set(data);
    }

    clearTaskData() {
        this._cancel$.next();          // abort any in-flight fetch
        this._taskData.set(null);
        this._loadingTaskId.set(null);
    }

    /**
     * Forces a refetch even when the store already holds this taskId.
     *
     * Use on entry to a screen: the SAME taskId can have new content (it may have
     * been assigned/claimed, or its stage changed) since the store cached it, and
     * the store must not be trusted as a cache for that.
     *
     * Still de-dupes a request already in flight for the same taskId; a request
     * for a different taskId cancels the previous one via `switchMap`.
     *
     * NOT safe to call from an effect that tracks `taskData` — it would loop.
     */
    reloadTaskData(taskId: string | null | undefined): void {
        if (!taskId) return;
        if (untracked(this._loadingTaskId) === taskId) return;

        this._loadingTaskId.set(taskId);
        this._requestTaskId$.next(taskId);
    }

    /**
     * Guarantees the store holds the task for `taskId`, fetching only when it
     * holds a DIFFERENT task (or none) — this is what fixes stale global state.
     *
     * Guarded so it is safe to drive from an effect: once the store holds the
     * requested taskId it stops fetching, so there is no refetch loop. Guards read
     * via `untracked` so callers only depend on the signals they read themselves.
     *
     * For freshness on screen entry use `reloadTaskData` instead.
     */
    ensureTaskData(taskId: string | null | undefined): void {
        if (!taskId) return;
        if (untracked(this._taskData)?.taskId === taskId) return;

        this.reloadTaskData(taskId);
    }
}
